import { X } from "lucide-react";

const STATUS_LABELS = {
  confirmed: "Confirmed",
  dispatched: "Dispatched",
  en_route: "Chauffeur En Route",
  chauffeur_arrived: "Chauffeur Arrived",
  passenger_loaded: "In Transit",
  completed: "Completed",
  cancelled: "Cancelled",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function BookingDetailDrawer({ booking, onClose }) {
  if (!booking) return null;

  const lines = booking.quote?.breakdown || [];

  return (
    <div
      data-testid="booking-detail-drawer"
      className="fixed inset-0 z-[60] flex"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex-1 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="w-full max-w-lg h-full overflow-y-auto bg-[#0B0B0C] border-l border-white/10 shadow-2xl animate-fade-in-up"
      >
        <div className="sticky top-0 z-10 bg-[#0B0B0C]/95 backdrop-blur-xl border-b border-white/10 px-8 py-5 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">
              Booking
            </div>
            <div
              data-testid="drawer-booking-id"
              className="font-mono text-xs text-white mt-1 break-all"
            >
              {booking.id}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="drawer-close-btn"
            className="text-[#8E9AA6] hover:text-white transition-colors p-1"
            aria-label="Close"
          >
            <X size={20} strokeWidth={1.25} />
          </button>
        </div>

        <div className="px-8 py-8 space-y-8">
          <div>
            <span
              data-testid="drawer-status-pill"
              className="inline-block text-[10px] uppercase tracking-[0.3em] text-[#0B0B0C] bg-[#E6E6E6] px-3 py-1.5"
            >
              {STATUS_LABELS[booking.status] || booking.status}
            </span>
            <div className="font-serif text-3xl text-white font-semibold tracking-tight mt-4">
              ${booking.total_price.toFixed(2)}
            </div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mt-1">
              {booking.vehicle_category.replace(/_/g, " ")} ·{" "}
              {booking.service_type.replace(/_/g, " ")}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5">
            <Row label="Pickup" value={formatDateTime(booking.pickup_datetime)} />
            <Row label="Pickup Address" value={booking.pickup_address} />
            {booking.dropoff_address && (
              <Row label="Drop-off Address" value={booking.dropoff_address} />
            )}
            {booking.duration_hours && (
              <Row label="Duration" value={`${booking.duration_hours} hours`} />
            )}
            <Row
              label="Passengers / Luggage"
              value={`${booking.passengers} pax · ${booking.luggage} bags`}
            />
            {booking.flight_number && (
              <Row label="Flight" value={booking.flight_number} />
            )}
            {booking.flight && booking.flight.valid && (
              <Row
                label="Flight Status"
                value={`${booking.flight.airline} · ${booking.flight.status} · Gate ${booking.flight.gate}`}
              />
            )}
            {booking.special_requests && (
              <Row label="Notes" value={booking.special_requests} />
            )}
            <Row label="Payment" value={booking.payment_status} />
            <Row label="Reserved" value={formatDateTime(booking.created_at)} />
          </div>

          <div className="border-t border-white/10 pt-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-4">
              Fare breakdown
            </div>
            <dl
              data-testid="drawer-breakdown"
              className="space-y-2 bg-[#121214]/60 border border-white/10 p-5 backdrop-blur-md"
            >
              {lines.map((l, i) => {
                const isTotal = l.label === "Total";
                return (
                  <div
                    key={i}
                    className={`flex items-baseline justify-between gap-3 ${
                      isTotal ? "pt-3 mt-2 border-t border-white/15" : ""
                    }`}
                  >
                    <div>
                      <dt
                        className={`${
                          isTotal
                            ? "text-white text-xs uppercase tracking-[0.2em] font-medium"
                            : "text-[#E6E6E6] text-sm"
                        }`}
                      >
                        {l.label}
                      </dt>
                      {l.note && (
                        <div className="text-[10px] text-[#8E9AA6] mt-0.5">{l.note}</div>
                      )}
                    </div>
                    <dd
                      className={`tabular-nums ${
                        isTotal
                          ? "font-serif text-xl text-white font-semibold"
                          : "text-sm text-white"
                      }`}
                    >
                      ${Number(l.amount).toFixed(2)}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1">
        {label}
      </div>
      <div className="text-sm text-white font-light">{value}</div>
    </div>
  );
}
